
import { useState } from "react";
import { useForm } from "react-hook-form";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { ScrollArea } from "@/components/ui/scroll-area";

interface MovementFormProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  productId: string;
  periodId: string;
}

interface FormValues {
  quantity: number;
  movement_type: 'entry' | 'exit' | 'expired' | 'initial';
  source_destination?: string;
  batch_number?: string;
  expiry_date?: string;
  remarks?: string;
}

export const MovementForm = ({ open, onOpenChange, productId, periodId }: MovementFormProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const queryClient = useQueryClient();
  
  const form = useForm<FormValues>({
    defaultValues: {
      quantity: 0,
      movement_type: 'entry',
    },
  });

  const onSubmit = async (values: FormValues) => {
    setIsLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast.error("Vous devez être connecté pour ajouter un mouvement");
        return;
      }

      // Vérifier d'abord si le produit existe
      const { data: product, error: productError } = await supabase
        .from('inventory_products')
        .select('id')
        .eq('id', productId)
        .single();

      if (productError || !product) {
        toast.error("Impossible de trouver le produit");
        return;
      }

      // Récupérer le dernier mouvement pour obtenir le stock actuel
      const { data: lastMovement } = await supabase
        .from('inventory_movements')
        .select('current_stock')
        .eq('product_id', productId)
        .eq('period_id', periodId)
        .order('created_at', { ascending: false })
        .limit(1)
        .single();

      const currentStock = lastMovement?.current_stock || 0;
      
      // Calculer le nouveau stock
      const newStock = values.movement_type === 'entry' || values.movement_type === 'initial'
        ? currentStock + values.quantity
        : currentStock - values.quantity;

      // Vérifier si le stock ne devient pas négatif pour les sorties
      if (newStock < 0 && values.movement_type !== 'entry' && values.movement_type !== 'initial') {
        toast.error("Le stock ne peut pas être négatif");
        return;
      }

      // Insérer le mouvement
      const { error: movementError } = await supabase
        .from("inventory_movements")
        .insert({
          product_id: productId,
          period_id: periodId,
          quantity: values.quantity,
          movement_type: values.movement_type,
          movement_date: new Date().toISOString().split('T')[0],
          source_destination: values.source_destination,
          batch_number: values.batch_number,
          expiry_date: values.expiry_date,
          remarks: values.remarks,
          created_by: user.id,
          current_stock: newStock,
        });

      if (movementError) throw movementError;

      toast.success("Le mouvement a été ajouté avec succès");
      
      // Invalider les requêtes pour mettre à jour l'interface
      queryClient.invalidateQueries({ queryKey: ["inventory-movements"] });
      queryClient.invalidateQueries({ queryKey: ["inventory-products"] });
      queryClient.invalidateQueries({ queryKey: ["product-stock"] });
      
      onOpenChange(false);
      form.reset();
    } catch (error) {
      console.error("Error in onSubmit:", error);
      toast.error("Impossible d'ajouter le mouvement");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px] h-[90vh]">
        <DialogHeader>
          <DialogTitle>Ajouter un mouvement</DialogTitle>
        </DialogHeader>
        <ScrollArea className="h-[calc(90vh-100px)] px-4">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              <FormField
                control={form.control}
                name="movement_type"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Type de mouvement</FormLabel>
                    <Select onValueChange={field.onChange} defaultValue={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Sélectionnez un type" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="entry">Entrée</SelectItem>
                        <SelectItem value="exit">Sortie</SelectItem>
                        <SelectItem value="expired">Périmé</SelectItem>
                        <SelectItem value="initial">Stock initial</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="quantity"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Quantité</FormLabel>
                    <FormControl>
                      <Input 
                        type="number" 
                        min={0}
                        required 
                        {...field}
                        onChange={e => field.onChange(parseInt(e.target.value))}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="source_destination"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Source/Destination</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="batch_number"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Numéro de lot</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="expiry_date"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Date de péremption</FormLabel>
                    <FormControl>
                      <Input type="date" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="remarks"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Remarques</FormLabel>
                    <FormControl>
                      <Textarea {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="flex justify-end gap-2">
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => onOpenChange(false)}
                >
                  Annuler
                </Button>
                <Button 
                  type="submit" 
                  disabled={isLoading}
                >
                  {isLoading ? "Ajout..." : "Ajouter"}
                </Button>
              </div>
            </form>
          </Form>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
};

